import {
  money,
  STATUS_CLASS,
  STATUS_LABEL,
} from "../../services/finance.service";
import type { Expense, FinanceLookups } from "../../types/finance.types";
import { useFinancePayeeNames } from "../../hooks/useFinancePayeeNames";

interface Props {
  expenses: Expense[];
  lookups?: FinanceLookups;
  eventTitles?: Map<string, string>;
  showEvent?: boolean;
  onOpen: (id: string) => void;
  empty: string;
}

export default function ExpenseTable({
  expenses,
  lookups,
  eventTitles,
  showEvent,
  onOpen,
  empty,
}: Props) {
  // Before the early return: hooks run on every render.
  const payee = useFinancePayeeNames(expenses.map((x) => x.payee_id));
  if (expenses.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">{empty}</p>;
  }
  const type = (code: string) =>
    lookups?.types.find((t) => t.code === code)?.name ?? code;
  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full divide-y divide-gray-200 text-sm"
        data-testid="finance-expense-table"
      >
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-500">
              Expense
            </th>
            {showEvent && (
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Event
              </th>
            )}
            <th className="px-3 py-2 text-left font-medium text-gray-500">
              Type
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">
              Paid to
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">
              Service date
            </th>
            <th className="px-3 py-2 text-right font-medium text-gray-500">
              Anticipated
            </th>
            <th className="px-3 py-2 text-right font-medium text-gray-500">
              Approved
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {expenses.map((x) => (
            <tr
              key={x.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => onOpen(x.id)}
            >
              <td className="px-3 py-2">
                <div className="font-medium text-primary-600">
                  {x.expense_no}
                </div>
                <div className="max-w-xs truncate text-xs text-gray-500">
                  {x.description}
                </div>
              </td>
              {showEvent && (
                <td className="px-3 py-2 text-gray-700">
                  {x.event_id ? (
                    (eventTitles?.get(x.event_id) ?? "Event")
                  ) : (
                    <span className="text-gray-400">No event</span>
                  )}
                </td>
              )}
              <td className="px-3 py-2 text-gray-700">
                {type(x.expense_type_code)}
              </td>
              <td className="px-3 py-2 text-gray-700">{payee(x.payee_id)}</td>
              <td className="px-3 py-2 text-gray-700">
                {x.anticipated_service_date}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {money(x.anticipated_amount)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {x.approved_amount !== null ? money(x.approved_amount) : "—"}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[x.status]}`}
                >
                  {STATUS_LABEL[x.status]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
